import { Injectable } from '@angular/core';

/**
 * PDF Viewport information from ng2-pdf-viewer
 * Represents the transformation matrix and scaling information for a PDF page
 */
export interface PDFPageViewport {
  /** Viewport width in screen pixels */
  width: number;
  /** Viewport height in screen pixels */
  height: number;
  /** Current zoom/scale factor */
  scale: number;
  /** Rotation angle in degrees (0, 90, 180, 270) */
  rotation: number;
  /** Horizontal offset in screen pixels */
  offsetX: number;
  /** Vertical offset in screen pixels */
  offsetY: number;
  /** Transformation matrix [a, b, c, d, e, f] */
  transform: number[];
}

/**
 * Point in exact PDF coordinates (immutable, device-independent)
 * PDF coordinates: exact values in PDF points, origin at bottom-left
 */
export interface PDFPoint {
  /** X coordinate in PDF points */
  x: number;
  /** Y coordinate in PDF points - PDF bottom-left origin */
  y: number;
}

/**
 * Point in screen pixel coordinates (calculated, device-dependent)
 * Screen coordinates: origin at top-left, depends on zoom/rotation/DPI
 */
export interface ScreenPoint {
  /** X coordinate in screen pixels */
  x: number;
  /** Y coordinate in screen pixels */
  y: number;
}

/**
 * Rectangle in exact PDF coordinates
 */
export interface PDFRect {
  x: number;      // X coordinate in PDF points
  y: number;      // Y coordinate in PDF points - PDF bottom-left origin
  width: number;  // Width in PDF points
  height: number; // Height in PDF points
}

/**
 * Rectangle in screen pixel coordinates
 */
export interface ScreenRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Service for converting coordinates between PDF user space and screen pixels
 * 
 * PDF User Space:
 * - Coordinate system used internally by PDF documents
 * - 1 unit = 1 point = 1/72 inch
 * - Origin at bottom-left corner of page
 * - Independent of zoom, rotation, device DPI
 * - Standard US Letter page: 612×792 points (8.5"×11")
 * 
 * Screen Coordinates:
 * - Coordinate system used by browser/canvas
 * - 1 unit = 1 pixel (depends on device DPI)
 * - Origin at top-left corner of viewport
 * - Changes with zoom, rotation, viewport size
 * - Used for mouse events, canvas positioning
 */
@Injectable({
  providedIn: 'root'
})
export class CoordinateTransformService {

  /**
   * Convert canvas-relative coordinates to exact PDF coordinates
   * Used when placing form elements based on canvas clicks
   *
   * @param canvasX Canvas X coordinate (relative to canvas element)
   * @param canvasY Canvas Y coordinate (relative to canvas element)
   * @param viewport PDF page viewport information
   * @param pageDimensions Page dimensions in PDF points
   * @returns Exact PDF coordinates in points with bottom-left origin
   */
  screenToPDF(canvasX: number, canvasY: number, viewport: PDFPageViewport, pageDimensions: { width: number; height: number }): PDFPoint {
    // Get canvas dimensions
    const canvasWidth = viewport.width;
    const canvasHeight = viewport.height;

    // Apply rotation transform if needed
    const rotatedCoords = this.applyInverseRotation(canvasX, canvasY, viewport.rotation, viewport);

    // Convert to proportional coordinates first
    const proportionalX = rotatedCoords.x / canvasWidth;
    const proportionalY = 1 - (rotatedCoords.y / canvasHeight); // Flip Y for bottom-left origin

    // Convert proportional to exact PDF coordinates
    const exactX = proportionalX * pageDimensions.width;
    const exactY = proportionalY * pageDimensions.height;

    return {
      x: Math.max(0, Math.min(pageDimensions.width, Math.round(exactX * 100) / 100)), // Ensure within page bounds
      y: Math.max(0, Math.min(pageDimensions.height, Math.round(exactY * 100) / 100)) // Ensure within page bounds
    };
  }

  /**
   * Convert exact PDF coordinates to canvas pixel coordinates
   * Used when positioning Fabric.js objects on canvas
   *
   * @param pdfX Exact PDF X coordinate in points
   * @param pdfY Exact PDF Y coordinate in points (bottom-left origin)
   * @param viewport PDF page viewport information
   * @param pageDimensions Page dimensions in PDF points
   * @returns Canvas coordinates for fabric object positioning
   */
  pdfToScreen(pdfX: number, pdfY: number, viewport: PDFPageViewport, pageDimensions: { width: number; height: number }): ScreenPoint {
    const canvasWidth = viewport.width;
    const canvasHeight = viewport.height;

    // Convert exact PDF coordinates to proportional first
    const proportionalX = pdfX / pageDimensions.width;
    const proportionalY = pdfY / pageDimensions.height;

    // Convert from proportional PDF coordinates (bottom-left) to canvas coordinates (top-left)
    // X: direct proportional conversion
    // Y: flip from bottom-left to top-left origin
    const canvasX = proportionalX * canvasWidth;
    const canvasY = (1 - proportionalY) * canvasHeight; // Flip Y from bottom-left to top-left

    // Apply rotation transform if needed
    const rotatedCoords = this.applyRotation(canvasX, canvasY, viewport.rotation, viewport);

    return {
      x: Math.round(rotatedCoords.x),
      y: Math.round(rotatedCoords.y)
    };
  }

  /**
   * Convert exact PDF rectangle to screen rectangle
   * Used when positioning form elements with width/height
   */
  pdfRectToScreen(pdfRect: PDFRect, viewport: PDFPageViewport, pageDimensions: { width: number; height: number }): ScreenRect {
    // Convert the top-left corner of the PDF rectangle (in PDF coordinates: bottom-left + height)
    // In exact PDF: y is bottom edge, y + height is top edge
    const topLeft = this.pdfToScreen(pdfRect.x, pdfRect.y + pdfRect.height, viewport, pageDimensions);

    // Calculate the render scale from viewport vs page dimensions
    // Use the same scale for both dimensions to maintain aspect ratio (squares stay square)
    // This assumes the PDF viewer fits the page to the viewport while maintaining aspect ratio
    const scale = viewport.width / pageDimensions.width;

    // Use SAME scale for both dimensions to preserve element proportions
    const screenWidth = pdfRect.width * scale;
    const screenHeight = pdfRect.height * scale;

    return {
      x: topLeft.x,
      y: topLeft.y,
      width: screenWidth,
      height: screenHeight
    };
  }

  /**
   * Convert screen rectangle to exact PDF rectangle
   * Used when creating form elements from screen interactions
   */
  screenRectToPDF(screenRect: ScreenRect, viewport: PDFPageViewport, pageDimensions: { width: number; height: number }): PDFRect {
    const canvasWidth = viewport.width;
    const canvasHeight = viewport.height;

    // Calculate the render scale (inverse of pdfRectToScreen)
    // Use the same scale for both dimensions to maintain aspect ratio
    const scale = canvasWidth / pageDimensions.width;

    // Convert screen position to PDF position
    // X: direct proportional conversion
    const proportionalX = screenRect.x / canvasWidth;
    const exactX = proportionalX * pageDimensions.width;

    // Y: flip from top-left to bottom-left origin
    const proportionalY = 1 - (screenRect.y + screenRect.height) / canvasHeight;
    const exactY = proportionalY * pageDimensions.height;

    // Convert screen dimensions to PDF dimensions using SAME scale for both
    const exactWidth = screenRect.width / scale;
    const exactHeight = screenRect.height / scale;

    return {
      x: Math.max(0, Math.min(pageDimensions.width - exactWidth, exactX)),
      y: Math.max(0, Math.min(pageDimensions.height - exactHeight, exactY)),
      width: Math.min(pageDimensions.width, exactWidth),
      height: Math.min(pageDimensions.height, exactHeight)
    };
  }

  /**
   * Create viewport information from ng2-pdf-viewer canvas
   * Extracts transformation data needed for coordinate conversion
   */
  createViewportFromCanvas(canvas: HTMLCanvasElement, zoom: number, rotation: number): PDFPageViewport {
    // Use CSS dimensions for correct coordinate space
    const cssWidth = canvas.clientWidth;
    const cssHeight = canvas.clientHeight;
    
    // Use the zoom parameter directly - it should match the PDF viewer's actual zoom
    // The PDF viewer should be honoring our zoom binding now that conflicting settings are removed
    console.log(`📐 Viewport creation: CSS(${cssWidth}x${cssHeight}), Zoom: ${zoom}, Rotation: ${rotation}`);
    
    return {
      width: cssWidth,
      height: cssHeight,
      scale: zoom, // Use provided zoom parameter
      rotation: rotation,
      offsetX: 0, // Canvas-relative coordinates don't need offset
      offsetY: 0, // Canvas-relative coordinates don't need offset
      transform: [zoom, 0, 0, zoom, 0, 0] // Use zoom parameter
    };
  }

  /**
   * Apply rotation transformation to coordinates
   * @private
   */
  private applyRotation(x: number, y: number, rotation: number, viewport: PDFPageViewport): { x: number, y: number } {
    if (rotation === 0) return { x, y };
    
    const centerX = viewport.width / (2 * viewport.scale);
    const centerY = viewport.height / (2 * viewport.scale);
    const angle = (rotation * Math.PI) / 180;
    
    // Translate to center
    const translatedX = x - centerX;
    const translatedY = y - centerY;
    
    // Apply rotation
    const rotatedX = translatedX * Math.cos(angle) - translatedY * Math.sin(angle);
    const rotatedY = translatedX * Math.sin(angle) + translatedY * Math.cos(angle);
    
    // Translate back
    return {
      x: rotatedX + centerX,
      y: rotatedY + centerY
    };
  }

  /**
   * Apply inverse rotation transformation to coordinates
   * @private
   */
  private applyInverseRotation(x: number, y: number, rotation: number, viewport: PDFPageViewport): { x: number, y: number } {
    if (rotation === 0) return { x, y };
    
    const centerX = viewport.width / (2 * viewport.scale);
    const centerY = viewport.height / (2 * viewport.scale);
    const angle = (-rotation * Math.PI) / 180; // Negative for inverse
    
    // Translate to center
    const translatedX = x - centerX;
    const translatedY = y - centerY;
    
    // Apply inverse rotation
    const rotatedX = translatedX * Math.cos(angle) - translatedY * Math.sin(angle);
    const rotatedY = translatedX * Math.sin(angle) + translatedY * Math.cos(angle);
    
    // Translate back
    return {
      x: rotatedX + centerX,
      y: rotatedY + centerY
    };
  }

  /**
   * Convert exact PDF coordinates to proportional coordinates
   * Useful for calculations that need proportional values
   */
  exactToProportional(exact: PDFPoint | PDFRect, pageDimensions: { width: number; height: number }): PDFPoint | PDFRect {
    if ('width' in exact) {
      // PDFRect
      return {
        x: exact.x / pageDimensions.width,
        y: exact.y / pageDimensions.height,
        width: exact.width / pageDimensions.width,
        height: exact.height / pageDimensions.height
      };
    } else {
      // PDFPoint
      return {
        x: exact.x / pageDimensions.width,
        y: exact.y / pageDimensions.height
      };
    }
  }

  /**
   * Convert proportional coordinates to exact PDF coordinates
   * Useful for legacy data or when exact coordinates are needed
   */
  proportionalToExact(proportional: PDFPoint | PDFRect, pageDimensions: { width: number; height: number }): PDFPoint | PDFRect {
    if ('width' in proportional) {
      // PDFRect
      return {
        x: proportional.x * pageDimensions.width,
        y: proportional.y * pageDimensions.height,
        width: proportional.width * pageDimensions.width,
        height: proportional.height * pageDimensions.height
      };
    } else {
      // PDFPoint
      return {
        x: proportional.x * pageDimensions.width,
        y: proportional.y * pageDimensions.height
      };
    }
  }

  /**
   * Validate PDF coordinates are within reasonable bounds
   * Uses page dimensions for bounds checking
   */
  validatePDFCoordinates(point: PDFPoint, pageDimensions: { width: number; height: number }): boolean {
    return point.x >= 0 && point.x <= pageDimensions.width && point.y >= 0 && point.y <= pageDimensions.height;
  }

  /**
   * Get standard PDF page dimensions in points
   */
  getStandardPageDimensions(size: 'letter' | 'a4' = 'letter'): { width: number, height: number } {
    switch (size) {
      case 'letter':
        return { width: 612, height: 792 }; // 8.5" × 11"
      case 'a4':
        return { width: 595, height: 842 }; // 210mm × 297mm
      default:
        return { width: 612, height: 792 };
    }
  }
}